import React, { useCallback, useMemo } from 'react'
import { IMapPosition, IMapStopProps, POSITION_TYPE, WithId } from 'transfix-maps/types'
import TrimbleMap, { TTrimbleMapProps } from '../TrimbleMap'
import { getSelectedPositionType } from './utils/getSelectedPositionType'

/**
 * Using any here because this is meant to be used with any type of array
 */
const EMPTY_ARRAY: any[] = []

export type TDriverLocationsTrimbleMapProps<
  TPosition extends WithId<IMapPosition> = WithId<IMapPosition>,
  TStop extends WithId<IMapStopProps> = WithId<IMapStopProps>
> = Omit<TTrimbleMapProps<TPosition, TStop>, 'onSelectPosition' | 'onSelectStop'> & {
  selectedPosition?: TPosition | null
  selectedStop?: TStop | null
  /**
   * Sets the style for the `selectedPosition` prop
   * @default (selectedPosition, index, id) => POSITION_TYPE.PING
   */
  getSelectedPositionType?: (
    selectedPosition: TPosition,
    index: number,
    id: string | number
  ) => POSITION_TYPE
  /**
   * @default () => {}
   */
  onSelectPosition?: (position: TPosition | null) => void
  /**
   * @default () => {}
   */
  onSelectStop?: (stop: TStop | null) => void
  /**
   * hides attraction names for high risk loads
   * @default false
   */
  hideNearbyAttractions?: boolean
}

function DriverLocationsTrimbleMap<
  TPosition extends WithId<IMapPosition> = WithId<IMapPosition>,
  TStop extends WithId<IMapStopProps> = WithId<IMapStopProps>
>({
  getPositionPopup,
  getStopPopup,
  onSelectPosition = () => {},
  onSelectStop = () => {},
  enableNavigation = true,
  getSelectedPositionType: getSelectedPositionTypeProp = getSelectedPositionType,
  positions: positionsProp = EMPTY_ARRAY,
  selectedPosition: selectedPositionProp = null,
  stops: stopsProp = EMPTY_ARRAY,
  mapStyle: mapStyleProp = 'transportation',
  isNewWave = true,
  fitBoundsOptions: fitBoundsOptionsProp,
  ...props
}: TDriverLocationsTrimbleMapProps<TPosition, TStop>) {
  const selectStopHandler: TTrimbleMapProps['onSelectStop'] = useCallback(
    event => {
      // clear selected position
      onSelectPosition(null)

      // Find the first feature available from click area
      const { features = [] } = event || {}
      const [firstFeature] = features || []
      const { properties } = firstFeature || {}

      // All positions have data.id available
      const { data } = properties || {}
      const parsedData = JSON.parse(data)
      const { id: newId } = parsedData || {}

      // find related stop
      const newSelectedStop = stopsProp.find(({ data }) => {
        const { id: stopId } = data || {}
        return String(stopId) === String(newId)
      })

      if (newSelectedStop) {
        onSelectStop(newSelectedStop)
      } else {
        onSelectStop(null)
      }
    },
    [onSelectPosition, onSelectStop, stopsProp]
  )

  const selectPositionHandler: TTrimbleMapProps['onSelectPosition'] = useCallback(
    event => {
      // clear selected stop
      onSelectStop(null)

      // Find the first feature available from click area
      const { features = [] } = event || {}
      const [firstFeature] = features || []
      const { properties } = firstFeature || {}

      // All positions have data.id available
      const { data = JSON.stringify({}) } = properties || {}
      const parsedData = JSON.parse(data)
      const { id: newId } = parsedData || {}

      // Find specific position
      const newSelectedPosition = positionsProp.find(({ data }) => {
        const { id: positionId } = data || {}
        return String(positionId) === String(newId)
      })

      // Update the selected position, or null if not found
      if (newSelectedPosition) {
        onSelectPosition(newSelectedPosition)
      } else {
        onSelectPosition(null)
      }
    },
    [onSelectPosition, onSelectStop, positionsProp]
  )

  /**
   * Map positions and manually override `type` property based on selected position
   */
  const positions = useMemo(() => {
    const { data } = selectedPositionProp || {}
    const { id: selectedPositionId = '' } = data || {}
    return positionsProp.map((position, index) => ({
      ...position,
      type: getSelectedPositionTypeProp(position, index, selectedPositionId),
    }))
  }, [getSelectedPositionTypeProp, positionsProp, selectedPositionProp])

  return (
    <TrimbleMap
      dataTest='DriverLocationsTrimbleMap'
      stops={stopsProp}
      positions={positions}
      onSelectPosition={selectPositionHandler}
      onSelectStop={selectStopHandler}
      getPositionPopup={getPositionPopup}
      getStopPopup={getStopPopup}
      mapStyle={mapStyleProp}
      enableNavigation={enableNavigation}
      fitBoundsOptions={{
        padding: 48,
        animate: false,
        ...fitBoundsOptionsProp,
      }}
      isNewWave={isNewWave}
      {...props}
    />
  )
}

export default DriverLocationsTrimbleMap
